import { Box, Toolbar, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import Sidebar, { DRAWER_WIDTH, DRAWER_WIDTH_COLLAPSED } from './Sidebar';
import Topbar from './Topbar';
import { useUiStore } from '@/store/uiStore';
import ErrorBoundary from '@/components/common/ErrorBoundary';
import { pageTransition } from '@/lib/motion';

/** Authenticated app shell: collapsible sidebar + top bar, with animated route transitions. */
export default function MainLayout() {
  const theme = useTheme();
  const location = useLocation();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const sidebarCollapsed = useUiStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUiStore((s) => s.toggleSidebar);
  const mobileNavOpen = useUiStore((s) => s.mobileNavOpen);
  const setMobileNav = useUiStore((s) => s.setMobileNav);

  const navWidth = isDesktop ? (sidebarCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH) : 0;

  const handleToggleNav = () => (isDesktop ? toggleSidebar() : setMobileNav(!mobileNavOpen));

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box component="nav" sx={{ width: { md: navWidth }, flexShrink: { md: 0 } }}>
        <Sidebar
          variant={isDesktop ? 'permanent' : 'temporary'}
          open={isDesktop ? true : mobileNavOpen}
          collapsed={sidebarCollapsed}
          onClose={() => setMobileNav(false)}
        />
      </Box>

      <Box sx={{ flexGrow: 1, width: { md: `calc(100% - ${navWidth}px)` }, minWidth: 0 }}>
        <Topbar onToggleNav={handleToggleNav} navWidth={navWidth} />
        <Toolbar />
        <Box
          component="main"
          sx={{ p: { xs: 2, md: 3 }, minHeight: 'calc(100vh - 64px)', maxWidth: 1440, mx: 'auto' }}
        >
          <ErrorBoundary key={location.pathname}>
            <AnimatePresence mode="wait">
              <motion.div
                key={location.pathname}
                initial={pageTransition.initial}
                animate={pageTransition.animate}
                exit={pageTransition.exit}
              >
                <Outlet />
              </motion.div>
            </AnimatePresence>
          </ErrorBoundary>
        </Box>
      </Box>
    </Box>
  );
}
